/**
 * Asset Layouts Tab - New Implementation
 *
 * Simplified version without language dictionary indirection.
 * Uses direct field values from the new GraphQL schema.
 */
import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Box,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Typography,
	Divider,
	Button,
	IconButton,
	Snackbar,
	Alert,
	Menu,
	MenuItem,
	ListItemIcon,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
} from "@mui/material";
import {
	Add as AddIcon,
	Edit as EditIcon,
	Close as CloseIcon,
	ContentCopy as CloneIcon,
	Delete as DeleteIcon,
} from "@mui/icons-material";
import {
	selectLayout,
	addAssetLayout,
	updateLayout,
	cloneAssetLayout,
	deleteAssetLayout,
} from "../redux/slice";
import AssetGridEditor from "../components/assetGridEditor.jsx";
import ZoneDetailsTable from "../components/zoneDetailsTable.jsx";
import AddLayoutDialog from "../components/assetLayouts/addLayoutDialog.jsx";
import EditLayoutDialog from "../components/assetLayouts/editLayoutDialog.jsx";
import { v4 as uuidv4 } from "uuid";

const AssetLayoutsTab = forwardRef((props, ref) => {
	const dispatch = useDispatch();
	const layoutListRef = useRef(null);

	// Get state from Redux - simplified structure without language dictionary
	const { zoneLayouts, inspectionDetails } = useSelector(
		(state) => state.inspectionBuilderRedux.config
	);

	// Alias for compatibility with zone-level inspectionTypes field
	const inspectionTypes = inspectionDetails;
	const { selectedLayoutId } = useSelector(
		(state) => state.inspectionBuilderRedux.ui
	);
	// Check if config has been loaded (originalConfig is set after loadConfig completes)
	const { loading, originalConfig } = useSelector(
		(state) => state.inspectionBuilderRedux
	);
	const configLoaded = originalConfig !== null;

	const [highlightedZoneIndex, setHighlightedZoneIndex] = useState(null);
	const [addLayoutDialogOpen, setAddLayoutDialogOpen] = useState(false);
	const [editLayoutDialogOpen, setEditLayoutDialogOpen] = useState(false);
	const [editingLayoutIndex, setEditingLayoutIndex] = useState(null);
	const [errorMessage, setErrorMessage] = useState(null);
	const [uploadError, setUploadError] = useState(null);

	// Context menu state
	const [contextMenu, setContextMenu] = useState(null);
	const [contextMenuLayoutIndex, setContextMenuLayoutIndex] = useState(null);

	// Delete confirmation dialog state
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deletingLayoutIndex, setDeletingLayoutIndex] = useState(null);

	// Expose scroll method to parent component
	useImperativeHandle(ref, () => ({
		scrollToLayout: (layoutIndex) => {
			// Select the layout first
			dispatch(selectLayout(layoutIndex));

			if (!layoutListRef.current) return;

			const listItems = layoutListRef.current.querySelectorAll("[data-layout-index]");
			const targetItem = Array.from(listItems).find(
				(item) => parseInt(item.getAttribute("data-layout-index")) === layoutIndex
			);

			if (targetItem) {
				const scrollContainer = layoutListRef.current.parentElement;

				if (scrollContainer) {
					const containerRect = scrollContainer.getBoundingClientRect();
					const itemRect = targetItem.getBoundingClientRect();
					const scrollTop = scrollContainer.scrollTop;
					const itemOffset = itemRect.top - containerRect.top + scrollTop;
					const centerOffset = (containerRect.height - itemRect.height) / 2;

					scrollContainer.scrollTo({
						top: itemOffset - centerOffset,
						behavior: "smooth",
					});
				}

				// Highlight effect
				const button = targetItem.querySelector(".MuiListItemButton-root");
				if (button) {
					button.style.transition = "background-color 0.3s ease";
					button.style.backgroundColor = "rgba(25, 118, 210, 0.2)";
					setTimeout(() => {
						button.style.backgroundColor = "";
					}, 1500);
				}
			}
		},
	}));

	// Auto-scroll when selection changes
	const prevSelectedLayoutId = useRef(selectedLayoutId);
	useEffect(() => {
		if (selectedLayoutId !== null && selectedLayoutId !== prevSelectedLayoutId.current) {
			const timer = setTimeout(() => {
				if (!layoutListRef.current) return;

				const listItems = layoutListRef.current.querySelectorAll("[data-layout-index]");
				const targetItem = Array.from(listItems).find(
					(item) => parseInt(item.getAttribute("data-layout-index")) === selectedLayoutId
				);

				if (targetItem) {
					const scrollContainer = layoutListRef.current.parentElement;

					if (scrollContainer) {
						const containerRect = scrollContainer.getBoundingClientRect();
						const itemRect = targetItem.getBoundingClientRect();
						const scrollTop = scrollContainer.scrollTop;
						const itemOffset = itemRect.top - containerRect.top + scrollTop;
						const centerOffset = (containerRect.height - itemRect.height) / 2;

						scrollContainer.scrollTo({
							top: itemOffset - centerOffset,
							behavior: "smooth",
						});
					}

					const button = targetItem.querySelector(".MuiListItemButton-root");
					if (button) {
						button.style.transition = "background-color 0.3s ease";
						button.style.backgroundColor = "rgba(25, 118, 210, 0.2)";
						setTimeout(() => {
							button.style.backgroundColor = "";
						}, 1500);
					}
				}
			}, 200);

			return () => clearTimeout(timer);
		}

		prevSelectedLayoutId.current = selectedLayoutId;
	}, [selectedLayoutId]);

	// Auto-select first layout if none selected
	useEffect(() => {
		if (selectedLayoutId === null && zoneLayouts && zoneLayouts.length > 0) {
			dispatch(selectLayout(0));
		}
	}, [zoneLayouts, selectedLayoutId, dispatch]);

	// Get selected layout by index
	const selectedLayout = selectedLayoutId !== null ? zoneLayouts[selectedLayoutId] : null;

	// Handle adding layout from template library
	const handleAddLayout = (templatePiece) => {
		setErrorMessage(null);

		// Note: Duplicate asset type validation happens on save, not here
		// This matches ect2-web behavior - allow adding, validate on save

		// Add layout with new UUID
		dispatch(
			addAssetLayout({
				...templatePiece,
				layoutId: uuidv4(),
			})
		);
	};

	// Handle layout editing
	const handleOpenEditDialog = (index) => {
		setEditingLayoutIndex(index);
		setEditLayoutDialogOpen(true);
	};

	const handleSaveLayoutEdit = async (updatedData, fileUploaded) => {
		if (editingLayoutIndex === null) return;

		// Note: Image upload to EVIR API has been removed
		// If a new file was selected, we can't upload it - just use the existing assetViewId
		if (fileUploaded && updatedData.assetViewId === "default-selected-id") {
			console.warn("Image upload is not supported - EVIR_API_V2 endpoint has been removed");
			setUploadError("Image upload is not currently supported.");
			return;
		}

		// Update layout - using direct field names
		dispatch(
			updateLayout({
				layoutIndex: editingLayoutIndex,
				updates: {
					layoutName: updatedData.name,
					assetType: updatedData.assetType,
					assetViewId: updatedData.assetViewId,
				},
			})
		);

		setEditLayoutDialogOpen(false);
		setEditingLayoutIndex(null);
	};

	const handleCloseEditDialog = () => {
		setEditLayoutDialogOpen(false);
		setEditingLayoutIndex(null);
	};

	// Context menu handlers
	const handleContextMenu = (event, index) => {
		event.preventDefault();
		event.stopPropagation();
		setContextMenu({
			mouseX: event.clientX - 2,
			mouseY: event.clientY - 4,
		});
		setContextMenuLayoutIndex(index);
	};

	const handleCloseContextMenu = () => {
		setContextMenu(null);
		setContextMenuLayoutIndex(null);
	};

	const handleCloneLayout = () => {
		if (contextMenuLayoutIndex !== null) {
			dispatch(cloneAssetLayout({ layoutIndex: contextMenuLayoutIndex }));
		}
		handleCloseContextMenu();
	};

	const handleEditFromContextMenu = () => {
		if (contextMenuLayoutIndex !== null) {
			handleOpenEditDialog(contextMenuLayoutIndex);
		}
		handleCloseContextMenu();
	};

	// Delete handlers
	const handleOpenDeleteDialog = (index) => {
		setDeletingLayoutIndex(index);
		setDeleteDialogOpen(true);
	};

	const handleCloseDeleteDialog = () => {
		setDeleteDialogOpen(false);
		setDeletingLayoutIndex(null);
	};

	const handleConfirmDelete = () => {
		if (deletingLayoutIndex !== null) {
			dispatch(deleteAssetLayout({ layoutIndex: deletingLayoutIndex }));
		}
		handleCloseDeleteDialog();
	};

	const handleDeleteFromContextMenu = () => {
		if (contextMenuLayoutIndex !== null) {
			handleOpenDeleteDialog(contextMenuLayoutIndex);
		}
		handleCloseContextMenu();
	};

	// Get layout display name - DIRECT field access (no language dictionary)
	const getLayoutName = (layout) => {
		// Use layoutName directly
		if (layout.layoutName) {
			return layout.layoutName;
		}
		// Fallback to assetType
		if (layout.assetType) {
			return Array.isArray(layout.assetType)
				? layout.assetType.join(", ")
				: layout.assetType;
		}
		// Final fallback
		return `Layout ${layout.layoutId || "Unknown"}`;
	};

	return (
		<Box className="asset-layouts-tab" sx={{ display: "flex", gap: 2, height: "600px" }}>
			{/* Left Sidebar - Layouts List */}
			<Box
				sx={{
					width: 250,
					borderRight: 1,
					borderColor: "divider",
					overflowY: "auto",
					flexShrink: 0,
				}}
			>
				<Box sx={{ p: 2, pb: 1 }}>
					<Typography variant="h6" sx={{ mb: 1 }}>
						Asset Layouts
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Select a layout to edit zones and components
					</Typography>
					{errorMessage && (
						<Box
							sx={{
								mb: 2,
								p: 1.5,
								bgcolor: "error.main",
								borderRadius: 1,
								display: "flex",
								justifyContent: "space-between",
								alignItems: "flex-start",
							}}
						>
							<Typography variant="body2" sx={{ flex: 1, pr: 1, color: "white" }}>
								{errorMessage}
							</Typography>
							<IconButton
								size="small"
								onClick={() => setErrorMessage(null)}
								sx={{ mt: -0.5, mr: -0.5, color: "white" }}
							>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Box>
					)}
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						fullWidth
						onClick={() => {
							setErrorMessage(null);
							setAddLayoutDialogOpen(true);
						}}
						size="small"
					>
						Template Library
					</Button>
				</Box>
				<Divider />

				<List sx={{ pt: 1, px: 2 }} ref={layoutListRef}>
					{zoneLayouts.map((layout, index) => (
						<ListItem
							key={layout.layoutId || index}
							disablePadding
							data-layout-index={index}
							onContextMenu={(e) => handleContextMenu(e, index)}
							secondaryAction={
								<Box sx={{ display: "flex", gap: 0.5 }}>
									<IconButton
										aria-label="edit"
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											handleOpenEditDialog(index);
										}}
									>
										<EditIcon fontSize="small" />
									</IconButton>
									<IconButton
										aria-label="delete"
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											handleOpenDeleteDialog(index);
										}}
										color="error"
									>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</Box>
							}
						>
							<ListItemButton
								selected={selectedLayoutId === index}
								onClick={() => dispatch(selectLayout(index))}
								sx={{ borderRadius: 1 }}
							>
								<ListItemText primary={getLayoutName(layout)} />
							</ListItemButton>
						</ListItem>
					))}
				</List>

				{/* Context Menu */}
				<Menu
					open={contextMenu !== null}
					onClose={handleCloseContextMenu}
					anchorReference="anchorPosition"
					anchorPosition={
						contextMenu !== null
							? { top: contextMenu.mouseY, left: contextMenu.mouseX }
							: undefined
					}
				>
					<MenuItem onClick={handleEditFromContextMenu}>
						<ListItemIcon>
							<EditIcon fontSize="small" />
						</ListItemIcon>
						Edit
					</MenuItem>
					<MenuItem onClick={handleCloneLayout}>
						<ListItemIcon>
							<CloneIcon fontSize="small" />
						</ListItemIcon>
						Clone below
					</MenuItem>
					<MenuItem onClick={handleDeleteFromContextMenu} sx={{ color: "error.main" }}>
						<ListItemIcon>
							<DeleteIcon fontSize="small" color="error" />
						</ListItemIcon>
						Delete
					</MenuItem>
				</Menu>

				{zoneLayouts.length === 0 && (
					<Box sx={{ p: 2, textAlign: "center" }}>
						{loading || !configLoaded ? (
							<CircularProgress size={24} />
						) : (
							<Typography variant="body2" color="text.secondary">
								No asset layouts found
							</Typography>
						)}
					</Box>
				)}
			</Box>

			{selectedLayout ? (
				<>
					{/* Middle Column - Zone Details Table */}
					<Box
						sx={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							overflowY: "auto",
							minWidth: 500,
						}}
					>
						<Box sx={{ pt: 2, mb: 2 }}>
							<Typography variant="h6">{getLayoutName(selectedLayout)}</Typography>
						</Box>

						<ZoneDetailsTable
							layout={selectedLayout}
							layoutIndex={selectedLayoutId}
							inspectionTypes={inspectionTypes}
							onZoneHover={setHighlightedZoneIndex}
							highlightedZoneIndex={highlightedZoneIndex}
						/>
					</Box>

					{/* Right Column - Asset Grid Editor */}
					<Box
						sx={{
							flex: "0 1 400px",
							display: "flex",
							flexDirection: "column",
							overflowY: "auto",
							minWidth: 300,
						}}
					>
						<AssetGridEditor
							layout={selectedLayout}
							layoutIndex={selectedLayoutId}
							inspectionTypes={inspectionTypes}
							highlightedZoneIndex={highlightedZoneIndex}
							onZoneHover={setHighlightedZoneIndex}
						/>
					</Box>
				</>
			) : (
				<Box
					sx={{
						flex: 1,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					{loading || !configLoaded ? (
						<CircularProgress />
					) : (
						<Typography variant="h6" color="text.secondary">
							{zoneLayouts.length === 0
								? "No asset layouts available"
								: "Select an asset layout from the left to begin editing"}
						</Typography>
					)}
				</Box>
			)}

			{/* Add Layout Dialog */}
			<AddLayoutDialog
				open={addLayoutDialogOpen}
				onClose={() => setAddLayoutDialogOpen(false)}
				onAdd={handleAddLayout}
			/>

			{/* Edit Layout Dialog */}
			<EditLayoutDialog
				open={editLayoutDialogOpen}
				onClose={handleCloseEditDialog}
				onSave={handleSaveLayoutEdit}
				layout={editingLayoutIndex !== null ? zoneLayouts[editingLayoutIndex] : null}
				allLayouts={zoneLayouts}
			/>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={deleteDialogOpen}
				onClose={handleCloseDeleteDialog}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description"
			>
				<DialogTitle id="delete-dialog-title">Delete Asset Layout</DialogTitle>
				<DialogContent>
					<DialogContentText id="delete-dialog-description">
						Are you sure you want to delete this asset layout
						{deletingLayoutIndex !== null && zoneLayouts[deletingLayoutIndex]
							? ` "${getLayoutName(zoneLayouts[deletingLayoutIndex])}"`
							: ""}
						? This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDeleteDialog}>No</Button>
					<Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
						Yes
					</Button>
				</DialogActions>
			</Dialog>

			{/* Upload Error Snackbar */}
			<Snackbar
				open={!!uploadError}
				autoHideDuration={6000}
				onClose={() => setUploadError(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert onClose={() => setUploadError(null)} severity="error" sx={{ width: "100%" }}>
					{uploadError}
				</Alert>
			</Snackbar>
		</Box>
	);
});

AssetLayoutsTab.displayName = "AssetLayoutsTab";

export default AssetLayoutsTab;
